import { useDispatch } from "react-redux";
import { setLayoutDashboardTitle } from "../../../store/ui-slice";
import { useContext, useEffect, useRef, useState } from "react";
import useAPIRequest from "../../../custom_hooks/simple/useAPIRequest";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import UiContext from "../../../store/ui-context";
import { ProgressSpinner } from "primereact/progressspinner";
import { useReactToPrint } from "react-to-print";
import HeaderTemplate from "./report-template-header";
import DetailTemplate from "./report-template-detail";
import TableHeaderTemplate from "./report-template-table-header";
import GroupHeaderTemplate from "./report-template-group-header";
import GroupFooterTemplate from "./report-template-group-footer";
import reformatListData from "./report-template-reformat-data";
import reformatListCopyData from "./report-template-reformat-copy-data";
import {
  tableWidth,
  title,
  generateDefaultReportParams,
  paramSettings,
  columnSettings,
} from "./report-template-setting";
import { confirmDialog } from "primereact/confirmdialog";
import ProductSelectorDialog from "../../../dialogs/selectors/ProductSelectorDialog";

const settings = {};
for (let columnSetting of columnSettings) {
  settings[columnSetting.field] = columnSetting;
}

const ReportSalesItemPage = () => {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
  });
  const uiContext = useContext(UiContext);
  const dispatch = useDispatch();

  const { requestGet, loading } = useAPIRequest();
  const [reportParam, setReportParam] = useState(generateDefaultReportParams());
  const [paging, setPaging] = useState({
    page: 1,
    take: 1000000,
    filter: "",
    total: 0,
  });
  const [paidListData, setPaidListData] = useState([]);
  const [freeListData, setFreeListData] = useState([]);
  const [paidRawData, setPaidRawData] = useState([]);
  const [freeRawData, setFreeRawData] = useState([]);
  const [listRawData, setListRawData] = useState([]);
  const [visibleProductSelector, setVisibleProductSelector] = useState(false);

  // use effect section

  useEffect(() => {
    dispatch(setLayoutDashboardTitle({ title }));
  }, []);

  const numberFormat = (number) => {
    return new Intl.NumberFormat("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number || 0);
  };

  const calculateTotal = (rawList) => {
    const qty = rawList.reduce(
      (acc, curr) => acc + Number(curr.total_quantity || 0),
      0
    );
    const subTotal = rawList.reduce(
      (acc, curr) => acc + Number(curr.sub_total || 0),
      0
    );
    const grandTotal = rawList.reduce(
      (acc, curr) => acc + Number(curr.grand_total || 0),
      0
    );
    const discount = subTotal - grandTotal;
    return { qty, subTotal, discount, grandTotal };
  };

  // API call section

  const fetchListData = async ({ page, take, filter }) => {
    if (loading) return;
    const _filter = filter ? filter : getFilter();

    return requestGet({
      fullUrl: `/api/report`,
      params: {
        prefix: "sales_per_product_v1",
        page: page,
        take: take,
        filter: _filter,
      },
      onSuccess: ({ pagination, message, data }) => {
        setPaging({
          ...paging,
          total: pagination["total"],
          page: pagination["page"],
          take: pagination["take"],
          filter: _filter,
        });

        const newData = data;
        const paidRaw = newData.filter((item) => Number(item.is_free) === 0);
        const freeRaw = newData.filter((item) => Number(item.is_free) === 1);

        const { list: _paidListData } = reformatListData(
          paidRaw,
          paging.page,
          paging.take
        );
        const { list: _freeListData } = reformatListData(
          freeRaw,
          paging.page,
          paging.take
        );

        setPaidListData(_paidListData);
        setFreeListData(_freeListData);
        setPaidRawData(paidRaw);
        setFreeRawData(freeRaw);
        setListRawData(newData);
      },
      onError: ({ message, data }) => uiContext.showErrorMsg("Error", message),
    });
  };

  // other function section

  const getFilter = () => {
    let filterArray = [];

    for (let setting of paramSettings) {
      if (!setting.field) continue;
      let singleFilter = undefined;
      const value = reportParam[setting.name];
      const nullable = setting.nullable ?? false;
      switch (setting.type) {
        case "date":
          if (value || nullable) {
            singleFilter = `${setting.field}:${setting.operator}:${
              value ? format(value, setting.format) : "null"
            }`;
          }
          break;
        default:
          if (value || nullable) {
            singleFilter = `${setting.field}:${setting.operator}:${value}`;
          }
          break;
      }
      filterArray.push(singleFilter);
    }

    if (
      reportParam &&
      reportParam.data_product &&
      reportParam.data_product.length > 0
    ) {
      const filterProductArr = [];
      for (const product of reportParam.data_product) {
        filterProductArr.push(`product_id:=:${product.id}`);
      }
      filterArray.push(`${filterProductArr.join("||")}`);
    }

    return filterArray.filter((el) => el).join(";");
  };

  const getCopyClipboardData = (listRaw) => {
    let result = "";

    if (listRaw && listRaw.length > 0) {
      const firstRow = listRaw[0];
      const listOfKeys = Object.keys(firstRow).filter(
        (key) =>
          !key.startsWith("data_") &&
          typeof firstRow[key] !== "object" &&
          !key.includes("id") &&
          !key.includes("_id")
      );

      for (let key of listOfKeys) {
        result += `${key}\t`;
      }
      result += "\n";

      for (let row of listRaw) {
        for (let key of listOfKeys) {
          const data = row[key];
          result += `${data}\t`;
        }
        result += "\n";
      }
    }

    return result;
  };

  // UI handler section

  const handleOnConfirmProductSelector = (event, selected) => {
    if (selected && selected.length >= 1) {
      setReportParam({
        ...reportParam,
        data_product: selected,
      });
      setVisibleProductSelector(false);
    } else {
      setReportParam({
        ...reportParam,
        data_product: [],
      });
      setVisibleProductSelector(false);
    }
  };

  const handleGenerateReport = () => {
    fetchListData({ page: paging.page, take: paging.take });
  };

  const handleCopyToClipboard = () => {
    const copyText = getCopyClipboardData(reformatListCopyData(listRawData));
    navigator.clipboard
      .writeText(copyText)
      .then(() => {
        uiContext.showSuccessMsg("Success", "Data berhasil di-copy");
      })
      .catch(() => {
        uiContext.showErrorMsg("Error", "Gagal meng-copy data");
      });
  };

  const handleOnClickBack = () => {
    const accept = async () => {
      navigate("../..");
    };

    const reject = () => {};

    confirmDialog({
      message: `Menutup bon ini, lanjutkan?`,
      header: "Konfirmasi ",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      acceptLabel: "Konfirmasi",
      rejectLabel: "Tidak",
      acceptClassName: "p-button-danger",
      accept,
      reject,
    });
  };

  // custom UI section

  return (
    <div className={`col-start-start p-4 gap-4 w-100`}>
      <ProductSelectorDialog
        single={false}
        visible={visibleProductSelector}
        onHide={() => setVisibleProductSelector(false)}
        onConfirm={handleOnConfirmProductSelector}
      />
      {/* report header section */}
      <div className="elevated-card-tight-vertical row-between-end !px-4 w-100">
        <div className="row-start-center gap-4">
          {/* filter - from */}
          <div className="col-start-start gap-1">
            <span className="text-sm font-semibold text-slate-500 tracking-wider ">
              Dari:
            </span>
            <Calendar
              showIcon
              className="max-w-[11rem]"
              value={reportParam.dari}
              onChange={(e) =>
                setReportParam({ ...reportParam, dari: e.value })
              }
              locale="id"
            />
          </div>
          {/* filter - sampai */}
          <div className="col-start-start gap-1">
            <span className="text-sm font-semibold text-slate-500 tracking-wider ">
              Sampai:
            </span>
            <Calendar
              showIcon
              className="max-w-[11rem]"
              value={reportParam.sampai}
              onChange={(e) =>
                setReportParam({ ...reportParam, sampai: e.value })
              }
              locale="id"
            />
          </div>
          {/* filter - product */}
          <div className="col-start-start gap-1">
            <span className="text-sm font-semibold text-slate-500 tracking-wider ">
              Product:
            </span>
            <div className="row-start-center gap-2">
              <Button
                className={`row-start-center min-w-[12rem] max-w-[20rem] grow bg-indigo-50 gap-2`}
                outlined
                onClick={() => {
                  setVisibleProductSelector(true);
                }}
              >
                <div className="row-between-center w-100 gap-2">
                  <span className="truncate">
                    {!reportParam.data_product ||
                    reportParam.data_product.length === 0
                      ? "Semua Product"
                      : reportParam.data_product.map((el) => el.name).join(" | ")}
                  </span>
                  <i className="pi pi-search" />
                </div>
              </Button>
              {reportParam.data_product &&
                reportParam.data_product.length > 0 && (
                  <Button
                    icon="pi pi-times"
                    className="p-button-rounded p-button-text p-button-danger"
                    onClick={() =>
                      setReportParam({ ...reportParam, data_product: [] })
                    }
                  />
                )}
            </div>
          </div>
        </div>
        <div className="row-end-end">
          <Button
            icon="pi pi-refresh"
            label="Generate"
            onClick={() => handleGenerateReport()}
          />
        </div>
      </div>
      {/* report control section */}
      <div className="elevated-card-tight-vertical row-between-center w-full">
        <div className="row-center-center gap-4">
          <Button
            className="max-h-[2rem] min-w-[2rem] bg-red-300 border-red-300"
            icon="pi pi-arrow-left"
            severity="danger"
            onClick={handleOnClickBack}
          />
        </div>
        <Divider layout="vertical" className="max-h-[0.9rem]" />
        <div className="row-center-center gap-3">
          <Button
            icon="pi pi-copy"
            className="p-button-outlined"
            label="COPY"
            onClick={() => handleCopyToClipboard()}
          />
          <Button
            icon="pi pi-print"
            className="p-button-outlined"
            label="PRINT"
            onClick={() => reactToPrintFn()}
          />
        </div>
      </div>

      {/* report content section  */}

      <div className="col-center-center w-full">
        {loading && (
          <div className="col-start-start bg-white shadow-md p-3 border border-zinc-300 w-[55rem]">
            <ProgressSpinner />
          </div>
        )}
        {!loading && (
          <div
            ref={contentRef}
            className={`printContent col-start-start bg-white shadow-md print:shadow-none p-3 border print:border-0 border-zinc-300 `}
          >
            <HeaderTemplate reportParam={reportParam} />

            {/* TABLE 1: PAID SALES */}
            <div className={`col-start-start w-full ${tableWidth} mt-4`}>
              <div className="bg-zinc-700 text-white font-semibold px-3 py-2 w-full text-sm tracking-wider uppercase">
                PAID SALES
              </div>
              <table className={`${tableWidth}`}>
                <thead>
                  <TableHeaderTemplate />
                </thead>
                <tbody>
                  {paidListData.length > 0 ? (
                    paidListData.map((el) => {
                      if (el.report_type === "group-header")
                        return <GroupHeaderTemplate key={el.mykey} {...el} />;
                      else if (el.report_type === "group-footer")
                        return <GroupFooterTemplate key={el.mykey} {...el} />;
                      else return <DetailTemplate key={el.mykey} {...el} />;
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-4 text-slate-500 text-sm italic border border-zinc-300 w-full"
                      >
                        Tidak ada data penjualan berbayar
                      </td>
                    </tr>
                  )}
                </tbody>
                {paidListData.length > 0 && (
                  <tfoot>
                    <tr
                      className={`row-start-start ${tableWidth} text-sm bg-zinc-100 font-bold text-slate-800 border-t-2 border-zinc-400`}
                    >
                      <td className="row-start-start flex-grow border-zinc-300 border border-t-0 px-2 py-3 pl-6">
                        GRAND TOTAL PAID
                      </td>
                      <td
                        className={`${settings["total_quantity"].layout} ${settings["total_quantity"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        {numberFormat(calculateTotal(paidRawData).qty)}
                      </td>
                      <td
                        className={`${settings["product_price"].layout} ${settings["product_price"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        -
                      </td>
                      <td
                        className={`${settings["sub_total"].layout} ${settings["sub_total"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        {numberFormat(calculateTotal(paidRawData).subTotal)}
                      </td>
                      <td
                        className={`${settings["discount"].layout} ${settings["discount"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        {numberFormat(calculateTotal(paidRawData).discount)}
                      </td>
                      <td
                        className={`${settings["grand_total"].layout} ${settings["grand_total"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        {numberFormat(calculateTotal(paidRawData).grandTotal)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <div className="h-[2.5rem]" />

            {/* TABLE 2: FREE / PACKAGE CLAIM */}
            <div className={`col-start-start w-full ${tableWidth}`}>
              <div className="bg-zinc-700 text-white font-semibold px-3 py-2 w-full text-sm tracking-wider uppercase">
                FREE / PACKAGE CLAIM
              </div>
              <table className={`${tableWidth}`}>
                <thead>
                  <TableHeaderTemplate />
                </thead>
                <tbody>
                  {freeListData.length > 0 ? (
                    freeListData.map((el) => {
                      if (el.report_type === "group-header")
                        return <GroupHeaderTemplate key={el.mykey} {...el} />;
                      else if (el.report_type === "group-footer")
                        return <GroupFooterTemplate key={el.mykey} {...el} />;
                      else return <DetailTemplate key={el.mykey} {...el} />;
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-4 text-slate-500 text-sm italic border border-zinc-300 w-full"
                      >
                        Tidak ada data pemakaian paket / gratis
                      </td>
                    </tr>
                  )}
                </tbody>
                {freeListData.length > 0 && (
                  <tfoot>
                    <tr
                      className={`row-start-start ${tableWidth} text-sm bg-zinc-100 font-bold text-slate-800 border-t-2 border-zinc-400`}
                    >
                      <td className="row-start-start flex-grow border-zinc-300 border border-t-0 px-2 py-3 pl-6">
                        GRAND TOTAL FREE
                      </td>
                      <td
                        className={`${settings["total_quantity"].layout} ${settings["total_quantity"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        {numberFormat(calculateTotal(freeRawData).qty)}
                      </td>
                      <td
                        className={`${settings["product_price"].layout} ${settings["product_price"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        -
                      </td>
                      <td
                        className={`${settings["sub_total"].layout} ${settings["sub_total"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        {numberFormat(calculateTotal(freeRawData).subTotal)}
                      </td>
                      <td
                        className={`${settings["discount"].layout} ${settings["discount"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        {numberFormat(calculateTotal(freeRawData).discount)}
                      </td>
                      <td
                        className={`${settings["grand_total"].layout} ${settings["grand_total"].width} border-zinc-300 border border-t-0 border-l-0 px-2 py-3`}
                      >
                        {numberFormat(calculateTotal(freeRawData).grandTotal)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <div className="h-[2rem]" />
          </div>
        )}
      </div>
    </div>
  );
};
export default ReportSalesItemPage;
